/**
 * @voltxampere/spice — Inductor codec (L)
 *
 * Berkeley SPICE3 syntax: `L{name} N+ N- value [IC=i0]`
 *
 * Examples:
 *   L1 in out 10m
 *   L2 a b 100u
 *   Lprim p1 p2 1m IC=0
 *
 * Schema mapping: `type: "inductor"`, params: `{ inductance,
 * initialCurrent? }`.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ok, err, } from "./_base.js";
import { parseSpiceNumber, formatSpiceValue } from "../units.js";
import { numericParam } from "./_param-helpers.js";
export const inductorCodec = {
    spiceLetter: "L",
    schemaTypes: ["inductor"],
    examples: ["L1 in out 10m", "L2 a b 100u", "Lprim p1 p2 1m IC=0"],
    parse(card, _ctx) {
        if (card.tokens.length < 3) {
            return err("L needs 2 nodes and a value");
        }
        const [na, nb, raw, ...extras] = card.tokens;
        if (na === undefined || nb === undefined || raw === undefined) {
            return err("L has incomplete tokens");
        }
        const value = parseSpiceNumber(raw);
        if (value === null) {
            return err(`L value '${raw}' is not a valid number`);
        }
        let initialCurrent;
        for (const extra of extras) {
            const m = /^IC=(.+)$/i.exec(extra);
            if (m?.[1] !== undefined) {
                const ic = parseSpiceNumber(m[1]);
                if (ic !== null)
                    initialCurrent = ic;
            }
        }
        const params = { inductance: value };
        if (initialCurrent !== undefined)
            params.initialCurrent = initialCurrent;
        const component = {
            id: card.identifier,
            type: "inductor",
            position: { x: 0, y: 0 },
            rotation: 0,
            flipped: false,
            label: card.identifier,
            params,
            terminals: [],
        };
        return ok(component, [na, nb]);
    },
    emit(component, terminals) {
        const l = numericParam(component.params.inductance, 1e-3);
        const ic = numericParam(component.params.initialCurrent, 0);
        const [a, b] = terminals;
        const base = `${component.id} ${a ?? "0"} ${b ?? "0"} ${formatSpiceValue(l)}`;
        if (ic !== 0)
            return `${base} IC=${formatSpiceValue(ic)}`;
        return base;
    },
    owns(schemaType) {
        return schemaType === "inductor";
    },
};
//# sourceMappingURL=inductor.js.map